import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LANGUAGES } from "@/hooks/useLanguage";
import { CATEGORIES } from "@/data/categories";

interface NavbarProps {
  lang: string;
}

export default function Navbar({ lang }: NavbarProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [catsOpen, setCatsOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  const isArticlePage =
    location.pathname.includes("/guide") || location.pathname.includes("/article/");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  // 在浅色页面（文章页）始终使用深色文字 + 实色背景，避免标题栏看不见
  const solid = scrolled || isArticlePage;

  const goToSection = (id: string) => {
    setMenuOpen(false);
    if (isArticlePage) {
      navigate(`/${lang}`);
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }, 120);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const switchLanguage = (code: string) => {
    setLangOpen(false);
    setMenuOpen(false);
    const path = isArticlePage
      ? location.pathname.replace(`/${lang}`, `/${code}`)
      : `/${code}`;
    navigate(path);
  };

  const currentLang = LANGUAGES.find((l) => l.code === lang)?.label ?? "English";

  const navBtn = `text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
    solid
      ? "text-foreground-700 hover:text-primary-600"
      : "text-background-200 hover:text-background-50"
  }`;

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      <div className="bg-primary-600 text-background-50 text-xs text-center py-2 px-4">
        {t("announce")}
      </div>
      <div
        className={`transition-all duration-300 ${
          solid
            ? "bg-background-50/95 backdrop-blur border-b border-background-200 shadow-sm"
            : "bg-transparent"
        }`}
      >
        <nav className="w-full px-4 md:px-6">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link
              to={`/${lang}`}
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => setMenuOpen(false)}
            >
              <span className="w-9 h-9 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center">
                <i className="ri-link text-lg"></i>
              </span>
              <span
                className={`font-heading font-bold text-lg tracking-tight whitespace-nowrap ${
                  solid ? "text-foreground-950" : "text-background-50"
                }`}
              >
                USFans
                <span className={solid ? "text-primary-600" : "text-primary-300"}>
                  Spreadsheet
                </span>
              </span>
            </Link>

          <div className="hidden md:flex items-center gap-7">
            <Link to={`/${lang}`} className={navBtn}>
              {t("nav.home")}
            </Link>
            <Link to={`/${lang}/article/buying-guide`} className={navBtn}>
              {t("nav.shopGuide")}
            </Link>
            <Link to={`/${lang}/guide`} className={navBtn}>
              {t("nav.guide")}
            </Link>
            <button onClick={() => goToSection("categories")} className={navBtn}>
              {t("nav.categories")}
            </button>
            <button onClick={() => goToSection("faq")} className={navBtn}>
              {t("nav.faq")}
            </button>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setLangOpen((v) => !v)}
                className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  solid
                    ? "bg-background-100 border-background-200 text-foreground-800 hover:border-primary-300"
                    : "bg-background-50/20 border-background-50/30 text-background-50 hover:bg-background-50/30"
                }`}
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-global-line text-base"></i>
                </span>
                <span>{currentLang}</span>
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-down-s-line text-sm"></i>
                </span>
              </button>
              {langOpen && (
                <div className="absolute right-0 mt-2 w-40 rounded-lg bg-background-50 border border-background-200 py-1 shadow-sm">
                  {LANGUAGES.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => switchLanguage(l.code)}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer ${
                        l.code === lang
                          ? "text-primary-600 font-semibold"
                          : "text-foreground-700"
                      }`}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => setMenuOpen((v) => !v)}
              className={`md:hidden w-10 h-10 flex items-center justify-center rounded-md border cursor-pointer ${
                solid
                  ? "bg-background-100 border-background-200 text-foreground-800"
                  : "bg-background-50/20 border-background-50/30 text-background-50"
              }`}
              aria-label="Menu"
            >
              <i className={menuOpen ? "ri-close-line text-lg" : "ri-menu-line text-lg"}></i>
            </button>
          </div>
          </div>

        {menuOpen && (
          <div className="md:hidden pb-5 flex flex-col gap-1 bg-background-50 border-t border-background-200 max-h-[75vh] overflow-y-auto">
            <Link
              to={`/${lang}`}
              className="px-3 py-3 rounded-md text-sm font-medium text-foreground-800 hover:bg-background-100"
              onClick={() => setMenuOpen(false)}
            >
              {t("nav.home")}
            </Link>
            <Link
              to={`/${lang}/article/buying-guide`}
              className="px-3 py-3 rounded-md text-sm font-medium text-foreground-800 hover:bg-background-100"
              onClick={() => setMenuOpen(false)}
            >
              {t("nav.shopGuide")}
            </Link>
            <Link
              to={`/${lang}/guide`}
              className="px-3 py-3 rounded-md text-sm font-medium text-foreground-800 hover:bg-background-100"
              onClick={() => setMenuOpen(false)}
            >
              {t("nav.guide")}
            </Link>
            <button
              onClick={() => goToSection("categories")}
              className="text-left px-3 py-3 rounded-md text-sm font-medium text-foreground-800 hover:bg-background-100"
            >
              {t("nav.categories")}
            </button>
            <button
              onClick={() => goToSection("faq")}
              className="text-left px-3 py-3 rounded-md text-sm font-medium text-foreground-800 hover:bg-background-100"
            >
              {t("nav.faq")}
            </button>

            {/* 手机端分类导航 */}
            <button
              onClick={() => setCatsOpen((v) => !v)}
              className="mt-2 flex items-center justify-between gap-2 px-3 py-3 rounded-md text-sm font-semibold text-foreground-900 bg-background-100"
            >
              <span>{t("dropdown.label")}</span>
              <span
                className={`w-5 h-5 flex items-center justify-center transition-transform ${
                  catsOpen ? "rotate-180" : ""
                }`}
              >
                <i className="ri-arrow-down-s-line"></i>
              </span>
            </button>
            {catsOpen && (
              <div className="grid grid-cols-2 gap-1.5 px-3 pt-2 pb-1">
                {CATEGORIES.map((c) => (
                  <a
                    key={c.key}
                    href={c.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 px-2.5 py-2.5 rounded-md text-sm text-foreground-700 bg-background-100 hover:bg-primary-50 transition-colors"
                  >
                    <span className="w-5 h-5 flex items-center justify-center text-primary-600">
                      <i className={`${c.icon} text-base`}></i>
                    </span>
                    <span className="whitespace-nowrap">{t(c.key)}</span>
                  </a>
                ))}
              </div>
            )}
          </div>
          )}
        </nav>
      </div>
    </header>
  );
}